import { darken, lighten, Theme, ThemeOptions } from '@mui/material'
import { grey } from '@mui/material/colors'

import { CARD_HEADER_MIN_HEIGHT, commonOptions } from './commonOptions'

const darkThemeTextColor = '#FFFFFF'

export const darkOptions = {
  palette: {
    mode: 'dark',
    secondary: {
      main: '#757575'
    },
    text: {
      primary: '#F5F5F5'
    },
    background: {
      default: '#202020',
      paper: '#383838'
    },
    textColour: darkThemeTextColor,
    breadCrumbBackground: '#242424',
    breadCrumbTextColor: darkThemeTextColor,
    breadCrumbBorder: '#5C5B5A',
    operatorTileBackground: '#3D3D3C',
    accordionHeaderBackground: '#3D3D3C',
    operatorTileText: darkThemeTextColor,
    contentContainerBackground: '#242424',
    operatorActionsText: darkThemeTextColor,
    header: {
      topBar: {
        backgroundColor: '#5C5B5A',
        color: darkThemeTextColor
      },
      titleBar: {
        backgroundColor: '#3D3D3D',
        color: darkThemeTextColor
      }
    },
    matchStatsTableTextColor: darkThemeTextColor,
    matchStatsTableSecondaryTextColor: darkThemeTextColor,
    inputLabel: darkThemeTextColor,
    accordion: {
      sportTitle: darkThemeTextColor,
      tournament: commonOptions.palette.consoleGrey[1000],
      match: commonOptions.palette.consoleGrey[1100],
      divider: grey[700],
      matchStatsInnerBackground: grey[800]
    },
    sportColors: {
      SOCCER: {
        color: '#5cc5f0',
        background: '#099bd950'
      },
      BASKETBALL: {
        color: '#b488c7',
        background: '#42175F50'
      },
      'TABLE-TENNIS': {
        color: '#6b9cf5',
        background: '#1847A450'
      },
      VOLLEYBALL: {
        color: '#e85bb3',
        background: '#cd007450'
      },
      SNOOKER: {
        color: '#7bcc5a',
        background: '#4AB40650'
      },
      'AMERICAN-FOOTBALL': {
        color: '#5fb55f',
        background: '#02850050'
      },
      'ICE-HOCKEY': {
        color: '#9ff5e8',
        background: '#6EEED850'
      },
      CRICKET: {
        color: '#d4af84',
        background: '#b5895550'
      },
      OTHER: {
        color: '#ffffffff',
        background: '#362e2e5a'
      }
    },
    platformColors: {
      ANDROID: {
        color: '#3DAC58',
        background: '#318A46'
      },
      IOS: {
        color: '#EBEDF3',
        background: '#989A9D'
      },
      CHROME: {
        color: '#FBC209',
        background: '#B68D06'
      },
      FIREFOX: {
        color: '#FF6C22',
        background: '#B44C18'
      },
      IE: {
        color: '#00D277',
        background: '#009052'
      },
      SAFARI: {
        color: '#2399F8',
        background: '#186BAD'
      },
      OTHER: {
        color: '#bdbdbd',
        background: '#7F7F7F'
      }
    },
    subtable: {
      headerRow: {
        backgroundColor: '#383838',
        'th:first-of-type': {
          pl: 11
        },
        'td:first-of-type': {}
      },
      contentRow: {
        backgroundColor: '#383838',
        'td:first-of-type': {
          pl: 11
        },
        'th:first-of-type': {}
      }
    },
    card: {
      header: {
        minHeight: CARD_HEADER_MIN_HEIGHT,
        backgroundColor: '#464646'
      }
    },
    productionReport: {
      actionButton: {
        backgroundColor: '#666666ff',
        hoverBackgroundColor: '#5c5c5cff'
      }
    },
    dateRangePicker: {
      borderRadius: 50,
      rangeBackground: 'rgba(255, 77, 0, 0.15)'
    },
    statistics: {
      panel: {
        backgroundColor: '#464646',
        header: {
          minHeight: CARD_HEADER_MIN_HEIGHT,
          backgroundColor: '#383838'
        }
      },
      card: {
        backgroundColor: '#383838'
      }
    },
    charts: {
      lineChart: {
        component: {
          '.MuiAreaElement-root': {
            opacity: 0.8
          },
          '.MuiLineElement-root': {
            opacity: 0.8
          }
        },
        yAxis: {
          color: ['#E76A66', '#FEA46C']
        }
      }
    },
    phoneInput: {
      buttonBackground: '#424242',
      hoverButtonBackground: '#383838',
      flagBorder: '#616161'
    },
    horizontalTable: {
      header: {
        backgroundColor: '#383838'
      },
      border: {
        color: '#383838'
      }
    },
    golfColours: {
      eagle: '#d85a6a',
      birdie: '#e8a5a0',
      bogey: '#8fb8d9',
      doubleBogey: '#4a9bc4'
    }
  },
  typography: {
    caption: {
      color: grey[400]
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.grey[300]
        })
      }
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSelect-root': {
            '&.Mui-disabled': {
              backgroundColor: darken(theme.palette.background.paper, 0.25)
            }
          }
        })
      }
    },

    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#505050 #777777',
          scrollbarWidth: 4,

          '&::-webkit-scrollbar': {
            backgroundColor: '#3b3b3b',
            width: 8,
            height: 8
          },

          '&::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            backgroundColor: '#656565',
            minHeight: 24
          }
        }
      }
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '4px'
        },
        icon: {
          alignSelf: 'center',
          color: '#e5e5e5 !important'
        },
        standardInfo: {
          fontSize: 14,
          backgroundColor: '#616161',
          color: '#e5e5e5'
        }
      }
    },

    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${lighten(theme.palette.background.default, 0.2)}`,
          borderTop: `1px solid ${lighten(theme.palette.background.default, 0.2)}`
        })
      }
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: lighten(theme.palette.background.default, 0.2)
        })
      }
    },

    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper
        })
      }
    },

    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: darken(theme.palette.text.primary, 0.2)
        })
      }
    },

    MuiCheckbox: {
      styleOverrides: {
        root: () => ({
          color: '#D2D2D2'
        })
      }
    },

    MuiButton: {
      variants: [
        {
          props: { variant: 'dashed' },
          style: ({ theme }: { theme: Theme }) => ({
            textTransform: 'none',
            border: '2px dashed #d3d3d3',
            backgroundColor: 'rgba(14,14,14,0)',
            borderRadius: 3,
            color: theme.palette.grey[400],
            borderStyle: 'dashed',
            borderColor: theme.palette.grey[500],
            transition: 'all 0.175s ease-in-out',
            '&:hover': {
              backgroundColor: theme.palette.grey[800],
              color: theme.palette.grey[400],
              transition: 'all 0.175s ease-in-out'
            }
          })
        }
      ]
    }
  }
} satisfies ThemeOptions
